/**
 * SearchInput, reusable autocomplete search box for Voom Parts Inc.
 *
 * Wraps <input> with:
 *  - 250ms debounced `product.suggest` calls (query >= 2 chars)
 *  - Focus-empty dropdown: recent searches (localStorage) + popular searches
 *  - Typing dropdown: suggested product cards + query completions
 *  - Keyboard navigation (arrow keys, enter, escape)
 *  - Mobile-first input hints
 *  - 60s per-prefix cache to avoid duplicate round-trips on backspace/retype
 *  - Analytics via trackEvent() on submit/selection
 *
 * Two variants:
 *  - "hero"   , full-size, rounded-[100px], matches Home hero aesthetic
 *  - "compact", 44px tap target, fits Navbar and mobile menu
 *
 * Products.tsx already wires product.suggest directly; this component will
 * supersede that block in a follow-up (// TODO: migrate to <SearchInput>).
 */

import { useEffect, useMemo, useRef, useState } from "react";
import { useLocation } from "wouter";
import { Search, Clock, TrendingUp, X, Package } from "lucide-react";
import { trpc } from "@/lib/trpc";
import { cn } from "@/lib/utils";
import { formatGHS, VEHICLE_MAKES } from "@shared/marketplace";
import { trackEvent } from "@/lib/voom-tracker";
import { useDebounce } from "@/hooks/useDebounce";
import { useRecentSearches } from "@/hooks/useRecentSearches";

// ── Types ────────────────────────────────────────────────────────────────

type SuggestItem = {
  type: "product" | "brand" | "make" | "category";
  label: string;
  id?: number;
};

// Product shape from product.search, we only read a handful of fields.
type ProductLite = {
  id: number;
  name: string;
  price: string | number;
  images?: string[] | null;
  vehicleMake?: string | null;
  vehicleModel?: string | null;
};

type DropdownRow =
  | { kind: "product"; product: ProductLite; section: "suggested_parts" }
  | { kind: "query"; query: string; section: "query_completion" }
  | { kind: "recent"; query: string; section: "recent" }
  | { kind: "popular"; query: string; section: "popular" };

export interface SearchInputProps {
  placeholder?: string;
  initialValue?: string;
  variant?: "hero" | "compact";
  onSubmit?: (query: string) => void;
  className?: string;
  /** Optional test id prefix for automated testing. */
  testId?: string;
}

// ── Constants ────────────────────────────────────────────────────────────

const MAKES_LOWER = new Set(VEHICLE_MAKES.map(m => m.toLowerCase()));
const MAX_RECENT_IN_DROPDOWN = 5;
const MAX_POPULAR_IN_DROPDOWN = 8;
const MAX_PRODUCT_CARDS = 4;
const MAX_QUERY_COMPLETIONS = 4;
const DEBOUNCE_MS = 250;
const PREFIX_CACHE_TTL_MS = 60_000;

// ── Prefix cache (module-level so all mounts share) ──────────────────────

type CacheEntry = {
  suggest: SuggestItem[];
  products: ProductLite[];
  ts: number;
};

const prefixCache = new Map<string, CacheEntry>();
const PREFIX_CACHE_MAX_SIZE = 50;

function cacheGet(prefix: string): CacheEntry | null {
  const key = prefix.trim().toLowerCase();
  const hit = prefixCache.get(key);
  if (!hit) return null;
  if (Date.now() - hit.ts > PREFIX_CACHE_TTL_MS) {
    prefixCache.delete(key);
    return null;
  }
  return hit;
}

function cacheSet(
  prefix: string,
  suggest: SuggestItem[],
  products: ProductLite[]
) {
  const key = prefix.trim().toLowerCase();
  // Evict oldest entries if cache exceeds max size
  if (prefixCache.size >= PREFIX_CACHE_MAX_SIZE) {
    const oldest = prefixCache.keys().next().value;
    if (oldest != null) prefixCache.delete(oldest);
  }
  prefixCache.set(key, { suggest, products, ts: Date.now() });
}

// ── Component ────────────────────────────────────────────────────────────

export default function SearchInput({
  placeholder = "Search parts...",
  initialValue = "",
  variant = "hero",
  onSubmit,
  className,
  testId = "search-input",
}: SearchInputProps) {
  const [, navigate] = useLocation();
  const [value, setValue] = useState(initialValue);
  const [open, setOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(-1);
  const {
    searches: recent,
    add: addRecent,
    clear: clearRecent,
  } = useRecentSearches();

  const wrapperRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const debounced = useDebounce(value, DEBOUNCE_MS);
  const trimmed = debounced.trim();
  const hasQuery = trimmed.length >= 2;

  // ── Data fetching ──────────────────────────────────────────────────────

  const cached = hasQuery ? cacheGet(trimmed) : null;

  // product.suggest → query completions + typed suggestion labels
  const suggestQuery = trpc.product.suggest.useQuery(
    { query: trimmed },
    {
      enabled: hasQuery && !cached,
      staleTime: PREFIX_CACHE_TTL_MS,
    }
  );

  // product.search (limit 4) → rich product cards with thumbnail/price
  const productsQuery = trpc.product.search.useQuery(
    { query: trimmed, limit: MAX_PRODUCT_CARDS },
    {
      enabled: hasQuery && !cached,
      staleTime: PREFIX_CACHE_TTL_MS,
    }
  );

  // Seed module cache once both queries complete for a given prefix.
  useEffect(() => {
    if (!hasQuery) return;
    if (cached) return;
    if (!suggestQuery.data || !productsQuery.data) return;
    cacheSet(
      trimmed,
      suggestQuery.data as SuggestItem[],
      (productsQuery.data.products as ProductLite[]) ?? []
    );
    // No dependency on cached itself, we only want to write once per prefix.
  }, [trimmed, hasQuery, cached, suggestQuery.data, productsQuery.data]);

  const popularQuery = trpc.product.popularSearches.useQuery(undefined, {
    staleTime: 5 * 60_000,
  });

  // ── Derived rows ───────────────────────────────────────────────────────

  const suggestItems: SuggestItem[] =
    cached?.suggest ?? suggestQuery.data ?? [];
  const searchProducts: ProductLite[] =
    cached?.products ?? (productsQuery.data?.products as ProductLite[]) ?? [];

  // Query completions = distinct product names matching the prefix,
  // dedup'd against what's already shown as a product card.
  const queryCompletions: string[] = useMemo(() => {
    if (!hasQuery) return [];
    const cardNames = new Set(searchProducts.map(p => p.name.toLowerCase()));
    const out: string[] = [];
    const seen = new Set<string>();
    for (const item of suggestItems) {
      const label = item.label.trim();
      if (!label) continue;
      const lower = label.toLowerCase();
      if (seen.has(lower)) continue;
      if (cardNames.has(lower)) continue;
      seen.add(lower);
      out.push(label);
      if (out.length >= MAX_QUERY_COMPLETIONS) break;
    }
    return out;
  }, [hasQuery, suggestItems, searchProducts]);

  const rows: DropdownRow[] = useMemo(() => {
    if (hasQuery) {
      return [
        ...searchProducts.map(
          p =>
            ({
              kind: "product",
              product: p,
              section: "suggested_parts",
            }) as DropdownRow
        ),
        ...queryCompletions.map(
          q =>
            ({
              kind: "query",
              query: q,
              section: "query_completion",
            }) as DropdownRow
        ),
      ];
    }
    // Empty state: popular + recent
    const popular = (popularQuery.data ?? []).slice(0, MAX_POPULAR_IN_DROPDOWN);
    const recentRows = recent
      .slice(0, MAX_RECENT_IN_DROPDOWN)
      .map(
        q => ({ kind: "recent", query: q, section: "recent" }) as DropdownRow
      );
    const popularRows = popular.map(
      q => ({ kind: "popular", query: q, section: "popular" }) as DropdownRow
    );
    return [...recentRows, ...popularRows];
  }, [hasQuery, searchProducts, queryCompletions, popularQuery.data, recent]);

  const loading =
    hasQuery &&
    !cached &&
    (suggestQuery.isFetching || productsQuery.isFetching) &&
    rows.length === 0;

  // ── Outside click / escape → close ─────────────────────────────────────

  useEffect(() => {
    if (!open) return;
    function onDocMouseDown(e: MouseEvent) {
      if (!wrapperRef.current) return;
      if (!wrapperRef.current.contains(e.target as Node)) {
        setOpen(false);
        setActiveIndex(-1);
      }
    }
    document.addEventListener("mousedown", onDocMouseDown);
    return () => document.removeEventListener("mousedown", onDocMouseDown);
  }, [open]);

  // Reset highlight when rows change
  useEffect(() => {
    setActiveIndex(-1);
  }, [trimmed, rows.length]);

  // ── Actions ────────────────────────────────────────────────────────────

  function doSubmit(
    query: string,
    source: {
      selectedProductId?: number;
      section: DropdownRow["section"] | "submit";
      position?: number;
    }
  ) {
    const q = query.trim();
    if (!q || q.length < 2) return;
    addRecent(q);
    setOpen(false);
    setActiveIndex(-1);

    try {
      trackEvent({
        eventType: "search_click",
        productId: source.selectedProductId,
        metadata: {
          prefix: trimmed || null,
          selected_query: q,
          selected_product_id: source.selectedProductId ?? null,
          position: source.position ?? null,
          section: source.section,
          variant,
        },
      });
      // Phase 5 canonical alias — additive. The buyer-side
      // server-fired "search" event covers /products submits via
      // product.search; here we cover the SearchInput submit path
      // (homepage hero + nav search) so the funnel's first stage isn't
      // blind to client-only navigations that don't immediately hit
      // product.search. See docs/analytics/event-taxonomy.md.
      trackEvent({
        eventType: "search_performed",
        productId: source.selectedProductId,
        metadata: {
          query: q,
          source: `search_input:${variant}`,
          make: null,
          model: null,
          year: null,
          category: null,
          region: null,
          resultCount: null,
        },
      });
    } catch {
      /* non-blocking */
    }

    if (onSubmit) {
      onSubmit(q);
      return;
    }
    // If the query is a "Make Model" compound (from the vehicle-prefix
    // suggest path), navigate with make/model URL params so filter chips
    // appear on the Products page. Otherwise, plain text search.
    const firstSpace = q.indexOf(" ");
    if (firstSpace > 0) {
      const firstWord = q.slice(0, firstSpace).toLowerCase();
      const rest = q.slice(firstSpace + 1).trim();
      if (MAKES_LOWER.has(firstWord) && rest) {
        const params = new URLSearchParams();
        const make = VEHICLE_MAKES.find(m => m.toLowerCase() === firstWord)!;
        params.set("make", make);
        params.set("model", rest);
        navigate(`/products?${params.toString()}`);
        return;
      }
    }
    navigate(`/products?search=${encodeURIComponent(q)}`);
  }

  function selectRow(row: DropdownRow, position: number) {
    if (row.kind === "product") {
      // Tapping a product card navigates to the product detail.
      try {
        trackEvent({
          eventType: "search_click",
          productId: row.product.id,
          metadata: {
            prefix: trimmed || null,
            selected_query: null,
            selected_product_id: row.product.id,
            position,
            section: row.section,
            variant,
          },
        });
      } catch {
        /* non-blocking */
      }
      setOpen(false);
      setActiveIndex(-1);
      // Also record the typed prefix as a recent search, the user clearly
      // searched something and found a product, so the prefix is worth
      // remembering.
      if (trimmed.length >= 2) {
        addRecent(trimmed);
      }
      navigate(`/products/${row.product.id}`);
      return;
    }
    if (row.kind === "query") {
      doSubmit(row.query, {
        section: "query_completion",
        position,
      });
      setValue(row.query);
      return;
    }
    // recent / popular
    doSubmit(row.query, {
      section: row.section,
      position,
    });
    setValue(row.query);
  }

  function handleKeyDown(e: React.KeyboardEvent<HTMLInputElement>) {
    if (e.key === "Escape") {
      setOpen(false);
      setActiveIndex(-1);
      return;
    }
    if (e.key === "ArrowDown") {
      if (rows.length === 0) return;
      e.preventDefault();
      setOpen(true);
      setActiveIndex(i => (i + 1) % rows.length);
      return;
    }
    if (e.key === "ArrowUp") {
      if (rows.length === 0) return;
      e.preventDefault();
      setActiveIndex(i => (i <= 0 ? rows.length - 1 : i - 1));
      return;
    }
    if (e.key === "Enter") {
      e.preventDefault();
      if (activeIndex >= 0 && rows[activeIndex]) {
        selectRow(rows[activeIndex], activeIndex);
      } else if (value.trim().length >= 2) {
        doSubmit(value, { section: "submit" });
      }
    }
  }

  // ── Styling helpers ────────────────────────────────────────────────────

  const isHero = variant === "hero";
  const inputClasses = cn(
    "w-full bg-white/95 text-foreground border border-white/30 backdrop-blur-xl",
    "placeholder:text-muted-foreground/60 focus:outline-none focus:ring-2 focus:ring-primary/30",
    "transition-all duration-200",
    isHero
      ? "h-12 sm:h-13 pl-11 pr-10 rounded-[100px] text-[16px] sm:text-sm shadow-[0_8px_32px_-6px_rgba(0,0,0,0.12)]"
      : "h-11 pl-10 pr-9 rounded-full text-sm shadow-[0_4px_16px_-4px_rgba(0,0,0,0.08)]"
  );

  // Dropdown sits below the input. On mobile, bottom-nav is 56-64px tall, so
  // we cap the dropdown height and allow internal scroll to avoid overlap.
  const dropdownClasses = cn(
    "absolute z-50 top-full left-0 right-0 mt-2",
    "rounded-2xl bg-white/95 backdrop-blur-xl border border-white/30",
    "shadow-[0_8px_32px_-6px_rgba(0,0,0,0.12)]",
    "overflow-hidden",
    "max-h-[min(70vh,calc(100vh-11rem))]"
  );

  // ── Render ─────────────────────────────────────────────────────────────

  return (
    <div
      ref={wrapperRef}
      className={cn("relative w-full", className)}
      role="combobox"
      aria-haspopup="listbox"
      aria-expanded={open}
      aria-owns={`${testId}-listbox`}
    >
      <div className="relative">
        <Search
          aria-hidden="true"
          className={cn(
            "absolute top-1/2 -translate-y-1/2 text-muted-foreground/70 pointer-events-none",
            isHero ? "left-4 h-4 w-4" : "left-3.5 h-4 w-4"
          )}
        />
        <input
          ref={inputRef}
          type="text"
          inputMode="search"
          enterKeyHint="search"
          autoCapitalize="off"
          autoCorrect="off"
          spellCheck={false}
          placeholder={placeholder}
          aria-label="Search for spare parts"
          aria-autocomplete="list"
          aria-controls={`${testId}-listbox`}
          aria-activedescendant={
            activeIndex >= 0 ? `${testId}-opt-${activeIndex}` : undefined
          }
          value={value}
          onChange={e => {
            setValue(e.target.value);
            setOpen(true);
          }}
          onFocus={() => setOpen(true)}
          onKeyDown={handleKeyDown}
          className={inputClasses}
          data-testid={testId}
        />
        {value.length > 0 && (
          <button
            type="button"
            aria-label="Clear search"
            onClick={() => {
              setValue("");
              setActiveIndex(-1);
              setOpen(true);
              inputRef.current?.focus();
            }}
            className={cn(
              "absolute top-1/2 -translate-y-1/2 text-muted-foreground/60",
              "hover:text-foreground rounded-full min-w-[44px] min-h-[32px]",
              "flex items-center justify-center",
              isHero ? "right-2" : "right-1.5"
            )}
            data-testid={`${testId}-clear`}
          >
            <X className="h-4 w-4" />
          </button>
        )}
      </div>

      {open && (
        <div
          id={`${testId}-listbox`}
          role="listbox"
          className={dropdownClasses}
          // Prevent input blur before click fires
          onMouseDown={e => e.preventDefault()}
        >
          <div className="max-h-[min(70vh,calc(100vh-11rem))] overflow-y-auto">
            {/* ── Did-you-mean row ─────────────────────────────────
                Surface the rewriter's suggestion at the top of the
                dropdown so users see "Did you mean Vitz?" while they're
                still typing — instead of submitting, hitting zero
                results, and only then discovering the correction. The
                value lives in product.search's response (productsQuery)
                because we already call it for the preview cards; no
                extra request. */}
            {hasQuery &&
              productsQuery.data?.rewriteInfo?.didYouMean &&
              !productsQuery.data.rewriteInfo.wasRewritten && (
                <button
                  type="button"
                  className="flex items-center gap-2 w-full px-4 py-2.5 text-sm bg-primary/5 hover:bg-primary/10 border-b border-border/10 text-left transition-colors"
                  data-testid="search-did-you-mean"
                  onClick={() => {
                    const suggestion =
                      productsQuery.data?.rewriteInfo?.didYouMean;
                    if (!suggestion) return;
                    setValue(suggestion);
                    if (onSubmit) onSubmit(suggestion);
                    setOpen(false);
                  }}
                >
                  <span className="text-muted-foreground tracking-wide">
                    Did you mean
                  </span>
                  <strong className="text-primary font-semibold">
                    {productsQuery.data.rewriteInfo.didYouMean}
                  </strong>
                  <span className="ml-auto text-primary/60 text-xs">↵</span>
                </button>
              )}

            {loading && (
              <div className="px-4 py-3 text-sm text-muted-foreground tracking-wide">
                Finding matches…
              </div>
            )}

            {/* ── Typing state ─────────────────────────────────────── */}
            {hasQuery && !loading && rows.length === 0 && (
              <div className="px-4 py-6 text-center">
                <p className="text-sm text-muted-foreground/80 tracking-wide">
                  No suggestions yet. Press Enter to search.
                </p>
              </div>
            )}

            {hasQuery && searchProducts.length > 0 && (
              <div className="py-2">
                <p className="px-4 pb-1 pt-1 text-[10px] tracking-widest uppercase text-muted-foreground/60">
                  Suggested parts
                </p>
                {searchProducts.map((p, i) => {
                  const globalIndex = i;
                  const active = globalIndex === activeIndex;
                  const thumb =
                    Array.isArray(p.images) && p.images.length > 0
                      ? p.images[0]
                      : null;
                  const vehicle = [p.vehicleMake, p.vehicleModel]
                    .filter(Boolean)
                    .join(" ");
                  return (
                    <button
                      key={`p-${p.id}`}
                      type="button"
                      id={`${testId}-opt-${globalIndex}`}
                      role="option"
                      aria-selected={active}
                      onMouseEnter={() => setActiveIndex(globalIndex)}
                      onClick={() =>
                        selectRow(
                          {
                            kind: "product",
                            product: p,
                            section: "suggested_parts",
                          },
                          globalIndex
                        )
                      }
                      className={cn(
                        "w-full flex items-center gap-3 px-4 py-2.5 text-left transition-colors",
                        active ? "bg-primary/10" : "hover:bg-muted/40"
                      )}
                      data-testid={`${testId}-product-${p.id}`}
                    >
                      <div className="h-10 w-10 rounded-lg bg-muted/40 overflow-hidden flex items-center justify-center shrink-0">
                        {thumb ? (
                          <img
                            src={thumb}
                            alt=""
                            width={40}
                            height={40}
                            loading="lazy"
                            decoding="async"
                            className="h-full w-full object-cover"
                          />
                        ) : (
                          <Package className="h-4 w-4 text-muted-foreground/40" />
                        )}
                      </div>
                      <div className="min-w-0 flex-1">
                        <p className="truncate text-sm text-foreground tracking-wide">
                          {p.name}
                        </p>
                        {vehicle && (
                          <p className="truncate text-[11px] text-muted-foreground/70 tracking-wide">
                            {vehicle}
                          </p>
                        )}
                      </div>
                      <span className="shrink-0 text-sm font-medium text-foreground tracking-wide">
                        {formatGHS(p.price)}
                      </span>
                    </button>
                  );
                })}
              </div>
            )}

            {hasQuery && queryCompletions.length > 0 && (
              <div className="py-2 border-t border-border/10">
                <p className="px-4 pb-1 pt-1 text-[10px] tracking-widest uppercase text-muted-foreground/60">
                  Query completions
                </p>
                {queryCompletions.map((q, i) => {
                  const globalIndex = searchProducts.length + i;
                  const active = globalIndex === activeIndex;
                  return (
                    <button
                      key={`q-${q}`}
                      type="button"
                      id={`${testId}-opt-${globalIndex}`}
                      role="option"
                      aria-selected={active}
                      onMouseEnter={() => setActiveIndex(globalIndex)}
                      onClick={() =>
                        selectRow(
                          {
                            kind: "query",
                            query: q,
                            section: "query_completion",
                          },
                          globalIndex
                        )
                      }
                      className={cn(
                        "w-full flex items-center gap-3 px-4 py-2 text-left transition-colors",
                        active ? "bg-primary/10" : "hover:bg-muted/40"
                      )}
                      data-testid={`${testId}-query-${i}`}
                    >
                      <Search className="h-3.5 w-3.5 text-muted-foreground/60 shrink-0" />
                      <span className="flex-1 truncate text-sm tracking-wide">
                        {q}
                      </span>
                    </button>
                  );
                })}
              </div>
            )}

            {/* ── Empty-state: recent + popular ────────────────────── */}
            {!hasQuery && rows.length === 0 && (
              <div className="px-4 py-6 text-center">
                <p className="text-sm text-muted-foreground/80 tracking-wide">
                  Search for any part, brand, make, or model.
                </p>
              </div>
            )}

            {!hasQuery && recent.length > 0 && (
              <div className="py-2">
                <div className="px-4 pb-1 pt-1 flex items-center justify-between">
                  <p className="text-[10px] tracking-widest uppercase text-muted-foreground/60 flex items-center gap-1">
                    <Clock className="h-3 w-3" /> Recent
                  </p>
                  <button
                    type="button"
                    onClick={clearRecent}
                    className="text-[10px] text-muted-foreground/50 hover:text-foreground transition-colors"
                    data-testid={`${testId}-clear-recent`}
                  >
                    Clear
                  </button>
                </div>
                <div className="px-3 pb-2 flex flex-wrap gap-2">
                  {recent.slice(0, MAX_RECENT_IN_DROPDOWN).map((term, i) => {
                    const globalIndex = i;
                    const active = globalIndex === activeIndex;
                    return (
                      <button
                        key={`r-${term}`}
                        type="button"
                        id={`${testId}-opt-${globalIndex}`}
                        role="option"
                        aria-selected={active}
                        onMouseEnter={() => setActiveIndex(globalIndex)}
                        onClick={() =>
                          selectRow(
                            { kind: "recent", query: term, section: "recent" },
                            globalIndex
                          )
                        }
                        className={cn(
                          "text-xs px-3 py-1.5 rounded-full tracking-wide transition-colors flex items-center gap-1",
                          active
                            ? "bg-primary/15 text-foreground"
                            : "bg-muted/40 hover:bg-primary/10 text-foreground/80"
                        )}
                        data-testid={`${testId}-recent-${i}`}
                      >
                        <Clock className="h-2.5 w-2.5 text-muted-foreground/40" />
                        {term}
                      </button>
                    );
                  })}
                </div>
              </div>
            )}

            {!hasQuery && (popularQuery.data?.length ?? 0) > 0 && (
              <div
                className={cn(
                  "py-2",
                  recent.length > 0 && "border-t border-border/10"
                )}
              >
                <p className="px-4 pb-1 pt-1 text-[10px] tracking-widest uppercase text-muted-foreground/60 flex items-center gap-1">
                  <TrendingUp className="h-3 w-3" /> Popular
                </p>
                <div className="px-3 pb-2 flex flex-wrap gap-2">
                  {(popularQuery.data ?? [])
                    .slice(0, MAX_POPULAR_IN_DROPDOWN)
                    .map((term, i) => {
                      const globalIndex =
                        Math.min(recent.length, MAX_RECENT_IN_DROPDOWN) + i;
                      const active = globalIndex === activeIndex;
                      return (
                        <button
                          key={`pop-${term}`}
                          type="button"
                          id={`${testId}-opt-${globalIndex}`}
                          role="option"
                          aria-selected={active}
                          onMouseEnter={() => setActiveIndex(globalIndex)}
                          onClick={() =>
                            selectRow(
                              {
                                kind: "popular",
                                query: term,
                                section: "popular",
                              },
                              globalIndex
                            )
                          }
                          className={cn(
                            "text-xs px-3 py-1.5 rounded-full tracking-wide transition-colors",
                            active
                              ? "bg-primary/15 text-foreground"
                              : "bg-muted/60 hover:bg-primary/10 text-foreground/80"
                          )}
                          data-testid={`${testId}-popular-${i}`}
                        >
                          {term}
                        </button>
                      );
                    })}
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
